import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { ds } from '../styles/designSystem';

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [isDark, setIsDark] = useState(() => {
    try {
      return localStorage.getItem('mi-mecanico-dark') === 'true';
    } catch { return false; }
  });

  useEffect(() => {
    document.body.setAttribute('data-theme', isDark ? 'dark' : 'light');
    try {
      localStorage.setItem('mi-mecanico-dark', isDark);
    } catch {}
  }, [isDark]);

  const toggleDark = useCallback(() => setIsDark(prev => !prev), []);

  const value = useMemo(() => ({ isDark, toggleDark }), [isDark, toggleDark]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useThemeMode = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) return { isDark: false, toggleDark: () => {} };
  return ctx;
};

/**
 * Hook que devuelve estilos comunes de formulario listos para usar.
 * Elimina la necesidad de definir inputStyle/labelStyle en cada página.
 *
 * Uso:
 *   const { inputStyle, labelStyle, textareaStyle } = useFormStyles();
 */
export function useFormStyles() {
  const { border, text, inputBg } = useThemeColors();
  const base = {
    width: '100%',
    padding: '12px 14px',
    border: `1.5px solid ${border}`,
    borderRadius: '6px',
    fontSize: '15px',
    backgroundColor: inputBg,
    color: text,
    outline: 'none',
    boxSizing: 'border-box',
  };
  return {
    inputStyle: base,
    textareaStyle: { ...base, resize: 'vertical', minHeight: '90px', lineHeight: '1.5' },
    labelStyle: {
      display: 'block',
      marginBottom: '6px',
      fontWeight: '600',
      fontSize: '13px',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
  };
}

/**
 * Hook que devuelve isDark, toggleDark y las variables de color del tema
 * en un solo destructuring, eliminando la necesidad de declarar bg/card/text/sub/border
 * en cada componente.
 *
 * Uso:
 *   const { isDark, bg, card, text, sub, border } = useThemeColors();
 */
export function useThemeColors() {
  const { isDark, toggleDark } = useThemeMode();
  return {
    isDark,
    toggleDark,
    bg:       isDark ? ds.dark.bg       : '#F7F9FB',
    card:     isDark ? ds.dark.card     : '#FFFFFF',
    text:     isDark ? ds.dark.text     : '#0F172A',
    sub:      isDark ? ds.dark.sub      : '#475569',
    border:   isDark ? ds.dark.border   : '#E2E8F0',
    elevated: isDark ? ds.dark.elevated : '#F2F4F6',
    inputBg:  isDark ? ds.dark.elevated : '#FFFFFF',
  };
}
